import { useState, useMemo } from 'react';
import { useRealtimeStock } from '@/hooks/useRealtimeStock';
import { getKoreanDay } from '@/lib/time';
import { 
  getTodayOperatingHours, 
  checkOrderAvailability,
  type Restaurant as BaseRestaurant 
} from '@/lib/operatingHours';
import { addMinutes } from '@/services/seller/discount/discount-time';
import type { 
  StoreCardProps, 
  BestOfferInfo, 
  SizeStyles,
  CardSize,
  Layout
} from './store-card.types';

export function useStoreCard(props: StoreCardProps) {
  const {
    id,
    name,
    bestmenu,
    isOpen,
    isNew,
    storeType: _storeType,
    isFavorite = false,
    selectedOrderType = 'DEADLINE_DISCOUNT',
    products,
    originalPrice,
    discountedPrice,
    discountRate,
    deadlineDiscountStartTime,
    deadlineDiscountEndTime,
    todayStart,
    todayEnd,
    todayOperatingHours,
    bestmenu1,
    bestmenu2,
    bestmenu3,
    size = 'medium',
    layout = 'vertical',
    ...restaurantProps
  } = props;

  const [favoriteState, setFavoriteState] = useState(isFavorite);
  const [realtimeStock, setRealtimeStock] = useState<Record<string, number>>({});
  
  // 상품 정보 맵 생성 (실시간 재고 훅에서 사용)
  const productInfoMap = useMemo(() => {
    if (!products) return new Map();
    
    return new Map(products.map(product => [product.id, {
      id: product.id,
      name: product.name,
      is_available: product.is_available ?? true
    }]));
  }, [products]);
  
  // 실시간 재고 구독
  const productIds = products?.map(p => p.id) || [];
  useRealtimeStock(productIds.length > 0 ? productIds : null, {
    onStockUpdate: (productId, currentStock) => {
      setRealtimeStock(prev => ({
        ...prev,
        [productId]: currentStock
      }));
    },
    productInfoMap,
    showToast: true
  });

  // 헬퍼 함수를 사용한 통합 영업시간 관리
  const restaurantData: BaseRestaurant = useMemo(() => ({
    id,
    name,
    bestmenu,
    is_open: isOpen,
    is_new: isNew,
    store_type: _storeType,
    deadline_discount_start_time: deadlineDiscountStartTime,
    deadline_discount_end_time: deadlineDiscountEndTime,
    reservation_discount_enabled: true, // 기본값
    todayOperatingHours,
    // 레거시 컬럼 지원
    ...(todayStart && { [`${['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][getKoreanDay()]}_start`]: todayStart }),
    ...(todayEnd && { [`${['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][getKoreanDay()]}_end`]: todayEnd }),
    ...restaurantProps
  }), [id, name, bestmenu, isOpen, isNew, _storeType, deadlineDiscountStartTime, deadlineDiscountEndTime, todayOperatingHours, todayStart, todayEnd, restaurantProps]);

  // 헬퍼 함수를 사용한 영업시간 정보
  const operatingHours = useMemo(() => {
    return getTodayOperatingHours(restaurantData);
  }, [restaurantData]);

  // Get best offer for the selected order type (cheapest product) and total stock for deadline 할인
  const getBestOffer = (): { priceInfo: BestOfferInfo | null; deadlineStockTotal: number | null } => {
    let deadlineStockTotal: number | null = null;

    if (products && products.length > 0) {
      // 모든 상품의 해당 주문유형 오퍼들 수집
      const now = new Date();
      const today = now.toISOString().split('T')[0]; // YYYY-MM-DD 형식
      const matchingOffers = products
        .map(product => {
          const offer = product.offers.find(o => {
            // 기본 활성화 체크
            if (o.offer_type !== selectedOrderType || !o.is_active) return false;
            
            // 마감할인의 경우 반드시 당일 등록 검증 (강화된 로직)
            if (o.offer_type === 'DEADLINE_DISCOUNT') {
              // valid_from이 있는 경우: 정확히 오늘 날짜인지 확인
              if (o.valid_from) {
                const validFromDate = new Date(o.valid_from).toISOString().split('T')[0];
                if (validFromDate !== today) return false;
              } else {
                // valid_from이 없는 경우: 당일 등록으로 간주하되, valid_until 체크
                // 이는 기존 데이터 호환성을 위한 처리
              }
              
              // valid_until이 있으면 만료 여부 확인
              if (o.valid_until) {
                const validUntil = new Date(o.valid_until);
                if (now > validUntil) return false;
              }
            } else {
              // 다른 주문 유형의 경우 기존 로직 유지
              if (o.valid_from || o.valid_until) {
                const validFrom = o.valid_from ? new Date(o.valid_from) : null;
                const validUntil = o.valid_until ? new Date(o.valid_until) : null;
                
                if (validFrom && now < validFrom) return false;
                if (validUntil && now > validUntil) return false;
              }
            }
            
            return true;
          });
          
          const currentStock = realtimeStock[product.id] ?? product.current_stock ?? 0;
          const isAvailable = (product.is_available ?? true) && currentStock > 0;
          
          return offer ? {
            originalPrice: product.original_price,
            discountedPrice: offer.discounted_price,
            discountRate: offer.discount_rate,
            currentStock,
            productId: product.id,
            offerId: offer.id,
            isAvailable,
            offer
          } : null;
        })
        .filter(Boolean) as Array<BestOfferInfo & { offer: any; isAvailable: boolean }>; // null 제거

      const availableOffers = matchingOffers.filter(item => item!.isAvailable); // 재고 있는 것만
      
      if (selectedOrderType === 'DEADLINE_DISCOUNT') {
        deadlineStockTotal = matchingOffers.reduce((sum, item) => sum + (item?.currentStock || 0), 0);
      }
      
      // 가격 순으로 정렬하여 가장 저렴한 것 반환
      availableOffers.sort((a, b) => a!.discountedPrice - b!.discountedPrice);
      
      return { priceInfo: availableOffers[0] || null, deadlineStockTotal };
    }
    
    // Fallback to legacy props
    if (originalPrice && discountedPrice && discountRate !== undefined) {
      return {
        priceInfo: {
          originalPrice,
          discountedPrice,
          discountRate,
          currentStock: 999,
          isAvailable: true
        },
        deadlineStockTotal
      };
    }
    
    if (selectedOrderType === 'DEADLINE_DISCOUNT') {
      return { priceInfo: null, deadlineStockTotal: 0 }; // 데이터 없으면 품절 배지용 0
    }

    return { priceInfo: null, deadlineStockTotal };
  };

  // 대표메뉴 3개를 조합하는 함수
  const getBestMenuText = () => {
    const menus = [];
    
    // bestmenu1, bestmenu2, bestmenu3이 있으면 사용
    if (bestmenu1) menus.push(bestmenu1);
    if (bestmenu2) menus.push(bestmenu2);
    if (bestmenu3) menus.push(bestmenu3);
    
    // 3개 필드가 모두 없으면 레거시 bestmenu 사용
    if (menus.length === 0 && bestmenu) {
      return bestmenu;
    }
    
    // | 로 구분하여 조합
    return menus.join(' | ');
  };

  // 실시간 주문 가능 여부 판단 (헬퍼 함수 사용)
  const isOrderAvailableNow = () => {
    if (!selectedOrderType) return isOpen;
    
    return checkOrderAvailability(restaurantData, selectedOrderType);
  };

  // HH:MM:SS → HH:MM 변환용 헬퍼
  const formatToHHMM = (time?: string | null) => {
    if (!time) return undefined;
    const parts = time.split(':');
    if (parts.length >= 2) {
      return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
    }
    return time;
  };

  // 주문 유형에 따른 정확한 시간 표시 (헬퍼 함수 사용)
  const getPickupTimeText = (priceInfo: BestOfferInfo | null) => {
    const currentOffer = priceInfo?.offer;

    // 마감할인: 주문 가능 여부와 무관하게 수령가능시간을 노출
    if (selectedOrderType === 'DEADLINE_DISCOUNT') {
      const pickupStart = formatToHHMM(currentOffer?.deadline_pickup_start_time)
        ?? (deadlineDiscountStartTime ? formatToHHMM(addMinutes(deadlineDiscountStartTime, 30)) : undefined)
        ?? formatToHHMM(operatingHours.openTime);
      const pickupEnd = formatToHHMM(currentOffer?.deadline_pickup_end_time)
        ?? formatToHHMM(deadlineDiscountEndTime)
        ?? formatToHHMM(operatingHours.closeTime);

      if (pickupStart && pickupEnd) return `${pickupStart} ~ ${pickupEnd}`;
      if (pickupStart) return pickupStart;
      if (pickupEnd) return pickupEnd;
      return props.pickupInstructions;
    }

    const orderAvailable = isOrderAvailableNow();
    
    // 주문 불가능한 경우 '주문마감' 텍스트 표시
    // 예약할인은 오늘 휴무여도 주문 가능하므로 operatingHours.isClosed 체크 제거
    if (!orderAvailable) {
      return '주문마감';
    }

    switch (selectedOrderType) {
      case 'RESERVATION_DISCOUNT':
        if (currentOffer?.reservation_pickup_start_time && currentOffer?.reservation_pickup_end_time) {
          // 이미 포맷팅된 시간 사용
          return `내일 ${currentOffer.reservation_pickup_start_time} ~ ${currentOffer.reservation_pickup_end_time}`;
        }
        // Fallback - 내일 영업시작 1시간 후부터 영업종료까지 (이미 HH:MM 형식)
        if (operatingHours.openTime && operatingHours.closeTime) {
          const [startHour, startMinute] = operatingHours.openTime.split(':').map(Number);
          const pickupStartHour = startHour + 1; // 영업시작 1시간 후
          const pickupStart = `${String(pickupStartHour).padStart(2, '0')}:${String(startMinute).padStart(2, '0')}`;
          return `내일 ${pickupStart} ~ ${operatingHours.closeTime}`;
        }
        return '내일 수령';
      
      case 'REGULAR_ORDER':
        // 일반주문의 경우 정확한 요일별 영업시간 표시 (브레이크타임 고려) - 이미 HH:MM 형식
        if (operatingHours.openTime && operatingHours.closeTime) {
          if (operatingHours.breakStartTime && operatingHours.breakEndTime) {
            return `${operatingHours.openTime} ~ ${operatingHours.closeTime} (브레이크 ${operatingHours.breakStartTime}~${operatingHours.breakEndTime})`;
          }
          return `${operatingHours.openTime} ~ ${operatingHours.closeTime}`;
        }
        // Fallback: 영업시간 정보가 없는 경우에만 안내 문구 사용
        return '영업시간 확인 필요';
      
      default:
        if (operatingHours.openTime && operatingHours.closeTime) {
          return `${operatingHours.openTime} ~ ${operatingHours.closeTime}`;
        }
        return props.pickupInstructions;
    }
  };

  // 주문 유형에 따른 시간 라벨 설정
  const getTimeLabel = () => {
    return selectedOrderType === 'REGULAR_ORDER' ? '영업시간' : '수령가능시간';
  };

  const getSizeStyles = (size: CardSize, layout: Layout): SizeStyles => {
    const sizes = {
      small: {
        imageHeight: layout === 'horizontal' ? '80px' : '120px',
        titleSize: '18px',
        descSize: '14px',
        priceSize: '16px',
        padding: '12px'
      },
      medium: {
        imageHeight: layout === 'horizontal' ? '120px' : '160px',
        titleSize: '24px',
        descSize: '16px',
        priceSize: '20px',
        padding: '16px'
      },
      large: {
        imageHeight: layout === 'horizontal' ? '160px' : '200px',
        titleSize: '28px',
        descSize: '18px',
        priceSize: '24px',
        padding: '20px'
      }
    };
    return sizes[size];
  };

  const { priceInfo, deadlineStockTotal } = getBestOffer();
  const pickupTimeText = getPickupTimeText(priceInfo);
  const orderAvailable = isOrderAvailableNow();
  const bestMenuText = getBestMenuText();
  const timeLabel = getTimeLabel();
  const sizeStyles = getSizeStyles(size, layout);
  const deadlineStockBadgeText = selectedOrderType === 'DEADLINE_DISCOUNT' && deadlineStockTotal !== null
    ? (deadlineStockTotal > 0 ? `오늘 ${deadlineStockTotal}개 남음` : '품절')
    : null;

  const handleFavoriteToggle = () => {
    const newFavoriteState = !favoriteState;
    setFavoriteState(newFavoriteState);
    props.onFavoriteToggle?.();
  };

  return {
    favoriteState,
    priceInfo,
    pickupTimeText,
    orderAvailable,
    bestMenuText,
    timeLabel,
    sizeStyles,
    deadlineStockBadgeText,
    handleFavoriteToggle
  };
}
